import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  "object",
  "embed",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const isVisible = (element) => {
  if (!(element instanceof HTMLElement)) return false;

  return element.getClientRects().length > 0;
};

const getFocusableElements = (container) => {
  if (!container) return [];

  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
    if (!(element instanceof HTMLElement)) return false;

    if (element.getAttribute("aria-hidden") === "true") return false;

    return isVisible(element);
  });
};

const getInitialFocusElement = (container, initialFocusRef) => {
  const customInitial = initialFocusRef?.current;

  if (customInitial instanceof HTMLElement && container.contains(customInitial)) {
    return customInitial;
  }

  const focusableElements = getFocusableElements(container);

  if (focusableElements.length > 0) {
    return focusableElements[0];
  }

  return container;
};

const FocusTrap = (props) => {
  const { enabled = false, restoreFocus = true, initialFocusRef = null, className = "", children } = props;

  const containerRef = useRef(null);
  const previousActiveElementRef = useRef(null);

  const onKeyDown = (event) => {
    if (event.key !== "Tab") return;

    const container = containerRef.current;

    const focusable = getFocusableElements(container);

    if (focusable.length === 0) {
      event.preventDefault();
      container.focus();
      return;
    }

    const activeElement = document.activeElement;
    const firstElement = focusable[0];
    const lastElement = focusable[focusable.length - 1];

    if (!container.contains(activeElement)) {
      event.preventDefault();
      if (event.shiftKey) {
        lastElement.focus();
      } else {
        firstElement.focus();
      }

      return;
    }

    if (event.shiftKey && (activeElement === firstElement || activeElement === container)) {
      event.preventDefault();
      lastElement.focus();

      return;
    }

    if (!event.shiftKey && activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const focusFirstElement = useCallback(() => {
    getInitialFocusElement(containerRef.current, initialFocusRef).focus();
  }, [initialFocusRef]);

  const onFocusIn = useCallback(
    (event) => {
      const target = event.target;

      if (!(target instanceof Node)) return;

      const container = containerRef.current;

      if (!container.contains(target)) {
        focusFirstElement();
      }
    },
    [focusFirstElement],
  );

  useEffect(() => {
    if (!enabled || typeof document === "undefined") return undefined;

    const container = containerRef.current;

    if (!container) return undefined;

    previousActiveElementRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    focusFirstElement();

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", onFocusIn);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusIn);

      if (!restoreFocus) return;

      const previousActiveElement = previousActiveElementRef.current;
      if (previousActiveElement instanceof HTMLElement && previousActiveElement.isConnected) {
        previousActiveElement.focus();
      }
    };
  }, [enabled, restoreFocus, initialFocusRef, focusFirstElement, onFocusIn]);

  return (
    <div ref={containerRef} tabIndex={-1} className={className}>
      {children}
    </div>
  );
};

FocusTrap.propTypes = {
  enabled: PropTypes.bool,
  restoreFocus: PropTypes.bool,
  initialFocusRef: PropTypes.shape({
    current: PropTypes.any,
  }),
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default FocusTrap;
